import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { useEffect, useRef } from "react";

export default function AboutPage() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-in");
          }
        });
      },
      { threshold: 0.15 }
    );

    const elements = sectionRef.current?.querySelectorAll(".reveal");
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-dark-bg">
      <Navbar />
      <main>
        {/* Hero Header */}
        <section className="pt-28 md:pt-36 pb-16 md:pb-20 bg-dark-bg">
          <div className="w-full px-6 md:px-10 lg:px-16 max-w-4xl">
            <p className="text-xs md:text-sm font-sans font-semibold tracking-[0.2em] text-gold uppercase mb-4">
              About Us
            </p>
            <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-medium text-charcoal-50 leading-tight tracking-tight-xl mb-6">
              Pure water is our purpose
            </h1>
            <p className="text-base md:text-lg text-charcoal-300 font-sans leading-relaxed max-w-2xl">
              Propre Purification is a family-owned business
              dedicated to bringing laboratory-grade water filtration to homes across
              the midwest.
            </p>
          </div>
        </section>

        {/* Story Section */}
        <section className="pb-16 md:pb-20 bg-dark-bg">
          <div className="w-full px-6 md:px-10 lg:px-16">
            <div className="flex flex-col lg:flex-row gap-12 lg:gap-16">
              <div className="lg:w-1/2">
                <div className="rounded-2xl overflow-hidden aspect-[4/3] border border-dark-border">
                  <img
                    src="https://static.readdy.ai/image/4423c9e7c5b9582be3899e7caeaa467a/443915266c84f1061d37e75b22841398.png"
                    alt="Family at a local community event"
                    className="w-full h-full object-cover object-top"
                    loading="lazy"
                  />
                </div>
              </div>
              <div className="lg:w-1/2 flex flex-col justify-center">
                <h2 className="font-serif text-2xl md:text-3xl font-semibold text-charcoal-50 mb-4">
                  Born from necessity, built on care
                </h2>
                <p className="text-base text-charcoal-300 font-sans leading-relaxed mb-4">
                  After a four year stint in Los Angeles, our owner looked to start a family in his home state of Iowa, but as he researched, the elevated cancer rates caused hesitance in his follow through. What first seemed like a road block, turned into a business. It was time to give back to the state that raised him. Thus, Propre Purification was born. A force meant to combat the heavy nitrate and pesticide loads, in the beautiful state of Iowa. The state where he was born and raised,
                </p>
                <p className="text-base text-charcoal-300 font-sans leading-relaxed mb-4">
                  Every system is backed by our support team who
                  understands and caters to water problems in the state of Iowa. We don't just ship
                  equipment — we help you assess your water, recommend the right solution, and
                  ensure everything works perfectly.
                </p>
                <p className="text-base text-charcoal-300 font-sans leading-relaxed">
                  Our mission is simple: every family deserves water free from harmful contaminants. Water the way God intended.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Name Pronunciation */}
        <section className="py-16 md:py-20 bg-charcoal-900 border-y border-dark-border">
          <div className="w-full px-6 md:px-10 lg:px-16 max-w-3xl mx-auto text-center">
            <h2 className="font-serif text-2xl md:text-3xl font-semibold text-charcoal-50 mb-3">
              How to Pronounce Propre
            </h2>
            <p className="font-serif text-xl md:text-2xl text-gold mb-4 italic tracking-wide">
              proh-pruh
            </p>
            <p className="text-base text-charcoal-300 font-sans leading-relaxed">
              Propre means <span className="text-charcoal-50 font-medium">"clean"</span> in French.
              It is the same word used by grandmothers in France to describe a home
              that is spotless, pure, and cared for. We chose it because clean water
              is the foundation of a clean life — and because every family deserves
              water that is truly <em>propre</em>.
            </p>
          </div>
        </section>

        {/* Values */}
        <section className="py-24 md:py-32 bg-dark-bg">
          <div className="w-full px-6 md:px-10 lg:px-16">
            <div className="text-center mb-16">
              <h2 className="font-serif text-3xl md:text-4xl font-semibold text-charcoal-50 mb-4">
                What we believe
              </h2>
              <p className="text-base text-charcoal-300 font-sans max-w-xl mx-auto">
                Three principles guide every decision we make and every system we deliver.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
              {[
                {
                  title: "Transparency First",
                  description:
                    "We test your water before recommending anything. You see the results. We come to a solution that is fit to your needs.",
                  icon: "ri-shield-check-line",
                },
                {
                  title: "Quality Obsession",
                  description:
                    "If we wouldn't drink from it, we won't sell it.",
                  icon: "ri-award-line",
                },
                {
                  title: "Local Commitment",
                  description:
                    "We're not a national chain. We're your neighbors. We fight the same water problems you do, and we're always a phone call away when you need us.",
                  icon: "ri-home-heart-line",
                },
              ].map((value) => (
                <div
                  key={value.title}
                  className="bg-dark-surface border border-dark-border rounded-2xl p-6 md:p-8"
                >
                  <div className="w-12 h-12 rounded-full bg-gold/10 flex items-center justify-center mb-5 border border-gold/20">
                    <i className={`${value.icon} text-gold text-xl`}></i>
                  </div>
                  <h3 className="font-serif text-xl font-semibold text-charcoal-50 mb-3">
                    {value.title}
                  </h3>
                  <p className="text-sm text-charcoal-300 font-sans leading-relaxed">
                    {value.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}